/**
 * Invoice detail composer-foot — slim, sticky bottom **summary** band.
 *
 * Previously this duplicated the top header's action group (Print /
 * Send WhatsApp / Record receipt / Issue). The duplication confused
 * operators ("which set is authoritative?") and made Send WhatsApp
 * look bigger than the rest because of a split-button quirk. As of
 * 2026-05-23 the bottom is **summary only** — top header is the
 * single command surface, bottom is the "where am I" anchor.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────┬──────────────┐
 *   │ STATUS · Customer · INV/####                     │  ₹43,227     │
 *   │                                                  │  outstanding │
 *   └──────────────────────────────────────────────────┴──────────────┘
 *
 * The amount sits on the right, larger than the rest, with a small
 * "outstanding" / "total" caption underneath. The left side carries
 * the context strip.
 */
import { paiseToInrExact } from "../../../lib/format.ts";
import type { InvoiceStatus } from "./invoice-detail-header.tsx";
import { statusLabel } from "./invoice-detail-header.tsx";

export interface InvoiceDetailFootProps {
  invoice: {
    status: InvoiceStatus;
    docNumber: string | null;
    totalPaise: string;
  };
  customerName: string;
  /**
   * Net amount still owed by the customer (`total − receipts −
   * credits`). Mirrors the figure shown on the rail's Money card so
   * the two surfaces never disagree. `null` when the invoice is in a
   * state where "outstanding" doesn't apply (draft, void, reversed,
   * paid) — the foot falls back to the gross total in that case.
   */
  outstandingPaise?: string | null;
}

export function InvoiceDetailFoot({
  invoice,
  customerName,
  outstandingPaise = null,
}: InvoiceDetailFootProps) {
  const isReceivable =
    invoice.status === "issued" ||
    invoice.status === "partially_paid" ||
    invoice.status === "overdue";
  const showsOutstanding = isReceivable && outstandingPaise !== null;
  const displayPaise = showsOutstanding ? outstandingPaise : invoice.totalPaise;
  const caption = showsOutstanding ? "outstanding" : statusCaption(invoice.status);

  return (
    <footer className="composer-foot composer-foot--summary" aria-label="Detail summary">
      <div className="composer-foot-context">
        <span className="status-tag">{statusLabel(invoice.status)}</span>
        <span aria-hidden className="sep">
          ·
        </span>
        <strong>{customerName}</strong>
        {invoice.docNumber ? (
          <>
            <span aria-hidden className="sep">
              ·
            </span>
            <span className="num">{invoice.docNumber}</span>
          </>
        ) : null}
      </div>
      <div className="composer-foot-amount">
        <span className="caption">{caption}</span>
        <span className="amount num">{paiseToInrExact(displayPaise)}</span>
      </div>
    </footer>
  );
}

/**
 * Tiny caption shown under the amount when "outstanding" doesn't apply.
 * Keeps the right-side block visually consistent — the bigger number
 * always has a one-word label under it.
 */
function statusCaption(status: InvoiceStatus): string {
  switch (status) {
    case "paid":
      return "paid";
    case "reversed":
      return "reversed";
    case "void":
      return "voided";
    case "draft":
      return "draft total";
    default:
      return "total";
  }
}
